import cx from 'classnames'
import React from 'react'

import { adjustTrackScore } from '../../actions/updateScore'
import { Rank, rankEnumReverse } from '../../constants/constants'
import { useAppDispatch, useAppSelector } from '../../hooks'
import modalsSlice from '../../reducers/modalsSlice'
import {
  selectCurrentActiveGamers,
  selectCurrentPlaylistSlug,
  selectCurrentTrackDifficulty,
  selectCurrentTrackInfo,
  selectRandomTrackStatus,
} from '../../selectors/current-play-selectors'
import { selectMatchInfo } from '../../selectors/match-selectors'
import { selectCurrentMatchSlug, selectCurrentUsername } from '../../selectors/session-selectors'
import { selectViewSettings } from '../../selectors/settings-selectors'
import { ScoreBonuses, TrackPath } from '../../types'
import { onModalContainerRef } from '../../util/scrolling'
import CloseIcon from '../widgets/CloseIcon'
import TrackBadge from '../widgets/TrackBadge'
import ModalBackdrop from './ModalBackdrop'

type Props = {
  onClose: () => void
}
type ScoreInfo = {
  trackScore: number
  bonuses: ScoreBonuses
  total: number
  gamerId: string
}

const GameOverModal = ({ onClose }: Props) => {
  const dispatch = useAppDispatch()
  const playlistSlug = useAppSelector(selectCurrentPlaylistSlug)
  const trackInfo = useAppSelector(selectCurrentTrackInfo)
  const username = useAppSelector(selectCurrentUsername)
  const compoundMatchSlug = useAppSelector(selectCurrentMatchSlug)
  const [matchOwner, matchSlug] = compoundMatchSlug.split('/')
  const {
    bonuses: { hiddenLyrics, randomTrack, scoredBlue },
  } = useAppSelector(selectMatchInfo(matchOwner, matchSlug))
  const difficulty = useAppSelector(selectCurrentTrackDifficulty)
  const gamers = useAppSelector(selectCurrentActiveGamers)
  const { isShowLyrics } = useAppSelector(selectViewSettings(0))
  const { isRandomTrack } = useAppSelector(selectRandomTrackStatus)
  const trackPath: TrackPath = {
    compoundPlayerSlug: '', // to be filled per player
    compoundMatchSlug: compoundMatchSlug,
    playlistSlug,
    trackSlug: trackInfo.slug,
  }
  const sortedGamers = gamers.sort((gamerA, gamerB) => {
    const scoreA = gamerA.playStatus.score
    const scoreB = gamerB.playStatus.score
    return scoreB - scoreA
  })
  const winningScore = sortedGamers[0].playStatus.topScore // supports ties
  const scoreInfo: ScoreInfo[] = sortedGamers.reduce((acc, gamer, index) => {
    const {
      playStatus: { topScoreRank, topScore }, // TODO: should this be running score rank?
      gamerId,
    } = gamer
    const scoreInfo: ScoreInfo = {
      trackScore: topScore,
      bonuses: { originalScore: topScore },
      total: topScore,
      gamerId,
    }
    const isWinner = topScore === winningScore
    if (!isShowLyrics) {
      const hiddenLyricBonus = isWinner ? hiddenLyrics.base + difficulty * hiddenLyrics.factor : 0
      scoreInfo.bonuses.hiddenLyrics = hiddenLyricBonus
      scoreInfo.total += hiddenLyricBonus
    }
    if (isRandomTrack) {
      const randomTrackBonus = isWinner ? randomTrack.base + difficulty * randomTrack.factor : 0
      scoreInfo.bonuses.randomTrack = randomTrackBonus
      scoreInfo.total += randomTrackBonus
    }
    if (topScoreRank === 'perfect') {
      const scoredBlueBonus = scoredBlue.base + difficulty * scoredBlue.factor
      scoreInfo.bonuses.scoredBlue = scoredBlueBonus
      scoreInfo.total += scoredBlueBonus
    }
    return [...acc, scoreInfo]
  }, [] as ScoreInfo[])

  const getTable = () => {
    const getBody = () => {
      return sortedGamers.map(
        ({ playStatus: { counters, topScoreRank }, playerName, gamerId }, index) => {
          const {
            trackScore,
            total,
            bonuses: { hiddenLyrics = 0, randomTrack = 0, scoredBlue = 0 },
          } = scoreInfo[index]

          return (
            <tr key={gamerId} className="player">
              <td className="name" title={gamerId}>
                {playerName || gamerId}
              </td>
              <td colSpan={4}>
                <div className="scoreBox">
                  <div className="counters">
                    {rankEnumReverse.map((rank) => {
                      return (
                        <div key={`counter-${rank}`} className={rank}>
                          {counters[rank as Rank]}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </td>
              <td>
                <div className="scoreBox">
                  <div className={cx('score', { [topScoreRank || '']: true })}>
                    {Math.round(trackScore)}
                  </div>
                </div>
              </td>
              <td>
                <div className="score">{scoredBlue || '-'}</div>
              </td>
              <td>
                <div className="score">{randomTrack || '-'}</div>
              </td>
              <td>
                <div className="score">{hiddenLyrics || '-'}</div>
              </td>
              <td>
                <div className="score total">{Math.round(total)}</div>
              </td>
            </tr>
          )
        }
      )
    }
    return (
      <table className="players">
        <thead>
          <tr>
            <th />
            <th colSpan={4} className="bonuses">
              lyrics
            </th>
            <th>track</th>
            <th colSpan={3} className="bonuses">
              bonuses
            </th>
            <th>total</th>
          </tr>
          <tr>
            <th></th>
            <th colSpan={4}>
              <div className="counters">
                <div>poor</div>
                <div>fair</div>
                <div>great</div>
                <div>perfect</div>
              </div>
            </th>
            <th></th>
            <th className="bonus">blue?</th>
            <th className="bonus">random?</th>
            <th className="bonus">hidden?</th>
            <th></th>
          </tr>
        </thead>
        <tbody>{getBody()}</tbody>
      </table>
    )
  }
  const onSave = () => {
    scoreInfo.forEach(({ bonuses, total, gamerId }) => {
      const trackPathWithPlayer: TrackPath = {
        ...trackPath,
        compoundPlayerSlug: `${username}/${gamerId}`,
      }
      dispatch(adjustTrackScore({ trackPath: trackPathWithPlayer, totalScore: total, bonuses }))
    })
    dispatch(modalsSlice.actions.toggleLeaderboardModal({ isOpen: true, initialTab: 1 }))
    onClose()
  }
  return (
    <ModalBackdrop isTop>
      <div className="modalContainer gameOverModal" ref={onModalContainerRef} tabIndex={0}>
        <CloseIcon onClose={onClose} />
        <div className="modalBody">
          <TrackBadge trackInfo={trackInfo} />
          {getTable()}
          <div className="save">
            <button onClick={onSave}>Apply Bonuses</button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  )
}

export default GameOverModal
